import apiClient from "utils/apiClient";
import {
  ACTOR_ID_HEADER_KEY,
  COMPOSER_SERVICE_BASE_URL,
  OLREADIA_SERVICE_BASE_URL,
  PROFILE_SERVICE_BASE_URL,
} from "constants";
import { message } from "antd";

const API_V1 = `${OLREADIA_SERVICE_BASE_URL}/api/v1`;
const PROFILE_API_V1 = `${PROFILE_SERVICE_BASE_URL}/api/v1`;
const COMPOSER_API_V1 = `${COMPOSER_SERVICE_BASE_URL}/api/v1`;
const SKIP_WORKSPACE_CONTEXT_HEADER = "O-SKIP-WORKSPACE-CONTEXT";
const FILE_ID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const MEDIA_SECTIONS = new Set(["documents", "photos", "certifications", "licenses"]);
const FILE_IDS_NOT_FOUND_PATTERN = /files?\s+with\s+fileids?.*not found/i;
const FILES_NOT_READY_MESSAGE =
  "Some uploaded files are not available yet. Please wait a few seconds and try again.";

const unwrap = (response) => response.data;

const withPagination = (params = {}) => {
  const { page = 0, size = 20, ...rest } = params;
  return { page, size, ...rest };
};

const withActor = (actorId) =>
  actorId ? { headers: { [ACTOR_ID_HEADER_KEY]: actorId } } : {};

const withActorHeaders = (actorId, headers = {}) => ({
  headers: {
    ...(actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {}),
    ...headers,
  },
});

const withGuestAuth = (authToken) =>
  authToken
    ? {
        Authorization: authToken.startsWith("Bearer ")
          ? authToken
          : `Bearer ${authToken}`,
      }
    : {};

const pageContent = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.jobOpportunities)) return data.jobOpportunities;
  if (Array.isArray(data?.opportunities)) return data.opportunities;
  return [];
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isFilesNotReadyError = (error) => {
  const status = error?.response?.status ?? error?.response?.data?.code;
  const message = String(
    error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "",
  );
  return (
    Number(status) === 404 &&
    (FILE_IDS_NOT_FOUND_PATTERN.test(message) ||
      message === FILES_NOT_READY_MESSAGE)
  );
};

const withFilesReadyRetry = async (fn, { retries = 5, delayMs = 900 } = {}) => {
  let lastError = null;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (!isFilesNotReadyError(error) || attempt >= retries) throw error;
      await sleep(delayMs * (attempt + 1));
    }
  }
  throw lastError;
};

const extractFileId = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^(https?:|data:|blob:|file:|content:)/i.test(text)) return "";
  const uuid = text.match(FILE_ID_PATTERN)?.[0];
  return uuid || text;
};

const documentForProfileMediaPatch = (document) => {
  if (typeof document === "string") {
    const id = extractFileId(document);
    return id ? { id } : null;
  }
  if (!document || typeof document !== "object") return null;
  const id = extractFileId(
    document.id || document.fileId || document.uuid || document.documentId,
  );
  if (!id) return null;
  return {
    id,
    ...(document.caption ? { caption: document.caption } : {}),
  };
};

const photoForProfileMediaPatch = (photo) => {
  if (typeof photo === "string") return extractFileId(photo);
  if (!photo || typeof photo !== "object") return "";
  return extractFileId(photo.fileId || photo.id || photo.uuid || photo.photoId);
};

const certificationForProfileMediaPatch = (certification) => {
  if (!certification || typeof certification !== "object") return null;
  return {
    credentialType: certification.credentialType || certification.type,
    name: certification.name,
    certificationNumber: certification.certificationNumber,
    issuer: certification.issuer,
    issueDate: certification.issueDate,
    expiryDate: certification.expiryDate,
    fileId: extractFileId(
      certification.fileId || certification.id || certification.uuid,
    ),
  };
};

const compactMediaObject = (item) =>
  Object.fromEntries(
    Object.entries(item || {}).filter(([, value]) => {
      if (typeof value === "string") return value.trim();
      return value !== undefined && value !== null;
    }),
  );

const mediaKey = (section, item) => {
  if (section === "photos") return photoForProfileMediaPatch(item);
  if (section === "documents")
    return documentForProfileMediaPatch(item)?.id || "";
  const certification = certificationForProfileMediaPatch(item);
  return (
    certification?.fileId ||
    certification?.certificationNumber ||
    certification?.name ||
    ""
  );
};

const normalizeMediaList = (section, list) => {
  if (section === "photos") {
    return pageContent(list).map(photoForProfileMediaPatch).filter(Boolean);
  }
  if (section === "documents") {
    return pageContent(list)
      .map(documentForProfileMediaPatch)
      .filter(Boolean)
      .map(compactMediaObject);
  }
  if (section === "certifications" || section === "licenses") {
    return pageContent(list)
      .map(certificationForProfileMediaPatch)
      .filter(Boolean)
      .map(compactMediaObject);
  }
  return pageContent(list);
};

const uniqueByMediaKey = (section, list) => {
  const seen = new Set();
  return normalizeMediaList(section, list).filter((item) => {
    const key = mediaKey(section, item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildProfileMediaPatchList = async ({
  section,
  submitted,
  latest,
  previous,
}) => {
  const latestItems = uniqueByMediaKey(section, latest);
  const submittedItems = uniqueByMediaKey(section, submitted);
  const previousItems =
    previous == null ? [] : uniqueByMediaKey(section, previous);
  const submittedKeys = new Set(
    submittedItems.map((item) => mediaKey(section, item)).filter(Boolean),
  );
  const deletedKeys = new Set(
    previousItems
      .map((item) => mediaKey(section, item))
      .filter((key) => key && !submittedKeys.has(key)),
  );
  const next = new Map();

  latestItems.forEach((item) => {
    const key = mediaKey(section, item);
    if (key && !deletedKeys.has(key)) next.set(key, item);
  });

  submittedItems.forEach((item) => {
    const key = mediaKey(section, item);
    if (key) next.set(key, item);
  });

  return Array.from(next.values());
};

const normalizePage = (data) => {
  const content = pageContent(data);
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return {
      ...data,
      content,
      totalElements:
        typeof data.totalElements === "number"
          ? data.totalElements
          : content.length,
      totalPages: typeof data.totalPages === "number" ? data.totalPages : 1,
      size: typeof data.size === "number" ? data.size : content.length,
      number: typeof data.number === "number" ? data.number : 0,
      first: typeof data.first === "boolean" ? data.first : true,
      last: typeof data.last === "boolean" ? data.last : true,
      numberOfElements:
        typeof data.numberOfElements === "number"
          ? data.numberOfElements
          : content.length,
      empty:
        typeof data.empty === "boolean" ? data.empty : content.length === 0,
    };
  }

  return {
    content,
    totalElements: content.length,
    totalPages: 1,
    size: content.length,
    number: 0,
    first: true,
    last: true,
    numberOfElements: content.length,
    empty: content.length === 0,
  };
};

const joinedParam = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(",");
  return value;
};

const jobOpportunitySearchParams = ({
  page = 0,
  size = 20,
  sort = "createdAt,desc",
  status = "OPEN",
  search,
  locationType,
  workSchedules,
  requiredEducationLevel,
  requiredYearsOfExperience,
  skills,
  specializations,
  workAreas,
} = {}) => {
  const params = {
    page,
    size,
    sort,
  };
  const trimmedSearch = search?.trim();
  const schedulesParam = joinedParam(workSchedules);
  const skillsParam = joinedParam(skills);
  const specializationsParam = joinedParam(specializations);
  const workAreasParam = joinedParam(workAreas);

  if (trimmedSearch) params.q = trimmedSearch;
  if (locationType) params.locationType = locationType;
  if (schedulesParam) params.workSchedules = schedulesParam;
  if (requiredEducationLevel)
    params.requiredEducationLevel = requiredEducationLevel;
  if (
    requiredYearsOfExperience != null &&
    String(requiredYearsOfExperience).trim()
  ) {
    params.requiredYearsOfExperience = String(requiredYearsOfExperience).trim();
  }
  if (skillsParam) params.skills = skillsParam;
  if (specializationsParam) params.specializations = specializationsParam;
  if (workAreasParam) params.workAreas = workAreasParam;

  const hasAdvancedSearch = Boolean(
    trimmedSearch ||
    locationType ||
    schedulesParam ||
    requiredEducationLevel ||
    (requiredYearsOfExperience != null &&
      String(requiredYearsOfExperience).trim()) ||
    skillsParam ||
    specializationsParam ||
    workAreasParam,
  );

  if (!hasAdvancedSearch && status) params.status = status;

  return { params, hasAdvancedSearch };
};

const normalizeOrganizationPayload = (data) => {
  if (!data || typeof data !== "object") return data;

  const payload = { ...data };
  delete payload.businessLocationType;
  delete payload.businessLocationTypeId;
  delete payload.businessLocationTypeCode;

  const organizationLocationTypeId =
    data.organizationLocationTypeId ||
    data.organizationLocationType?.id ||
    data.businessLocationTypeId ||
    data.businessLocationType?.id ||
    data.businessLocationType ||
    null;

  return {
    ...payload,
    ...(organizationLocationTypeId ? { organizationLocationTypeId } : {}),
  };
};

const normalizeBusinessLocationPayload = (data) => {
  if (!data || typeof data !== "object") return data;

  const payload = { ...data };
  delete payload.businessLocationType;
  delete payload.businessLocationTypeId;
  delete payload.businessLocationTypeCode;

  const organizationLocationTypeId =
    data.organizationLocationTypeId ||
    data.organizationLocationType?.id ||
    data.businessLocationTypeId ||
    data.businessLocationType?.id ||
    data.businessLocationType ||
    null;

  return {
    ...payload,
    ...(organizationLocationTypeId ? { organizationLocationTypeId } : {}),
  };
};

const normalizeOrganizationResponse = (organization) => {
  if (!organization || typeof organization !== "object") return organization;

  return {
    ...organization,
    businessLocationType:
      organization.businessLocationType ||
      organization.organizationLocationType,
    businessLocations:
      organization.businessLocations ||
      organization.organizationLocations ||
      [],
    users: organization.users || organization.organizationUsers || [],
  };
};

const normalizeOrganizationPage = (page) => {
  if (Array.isArray(page)) return page.map(normalizeOrganizationResponse);
  if (!page || typeof page !== "object") return page;
  return {
    ...page,
    content: Array.isArray(page.content)
      ? page.content.map(normalizeOrganizationResponse)
      : page.content,
  };
};

const normalizeOrganizationLocationResponse = (location) => {
  if (!location || typeof location !== "object") return location;

  return {
    ...location,
    businessLocationType:
      location.businessLocationType || location.organizationLocationType,
    organizationId: location.organizationId || location.organizationId,
  };
};

const normalizeOrganizationLocationPage = (page) => {
  if (Array.isArray(page))
    return page.map(normalizeOrganizationLocationResponse);
  if (!page || typeof page !== "object") return page;
  return {
    ...page,
    content: Array.isArray(page.content)
      ? page.content.map(normalizeOrganizationLocationResponse)
      : page.content,
  };
};

export const olreadiaService = {
  workspaceUses: {
    getForWorkspace: async (workspaceId, actorId) =>
      apiClient
        .get(
          `${API_V1}/workspace-uses/workspace/${encodeURIComponent(workspaceId)}`,
          withActor(actorId),
        )
        .then(unwrap),
    saveForWorkspace: async ({ workspaceId, workspaceUse, actorId } = {}) =>
      apiClient
        .post(
          `${API_V1}/workspace-uses`,
          { workspaceId, workspaceUse },
          withActor(actorId),
        )
        .then(unwrap),
  },

  appStart: {
    catalog: async ({ actorId, authToken, workspaceUse = "JOB_SEARCH" } = {}) =>
      apiClient
        .get(`${API_V1}/olreadia-app-start`, {
          params: { workspaceUse },
          ...withActorHeaders(actorId, {
            [SKIP_WORKSPACE_CONTEXT_HEADER]: "true",
            ...withGuestAuth(authToken),
          }),
        })
        .then(unwrap),

    popularSearches: async ({ actorId, authToken, size = 50 } = {}) => {
      const params = new URLSearchParams({
        active: "true",
        page: "0",
        size: String(size),
      });
      params.append("sort", "ranking,asc");
      params.append("sort", "searchCount,desc");

      return apiClient
        .get(`${API_V1}/popular-searches`, {
          params,
          ...withActorHeaders(actorId, {
            [SKIP_WORKSPACE_CONTEXT_HEADER]: "true",
            ...withGuestAuth(authToken),
          }),
        })
        .then(unwrap)
        .then(pageContent);
    },

    popularLocations: async ({ actorId, authToken, size = 50 } = {}) => {
      const params = new URLSearchParams({
        active: "true",
        page: "0",
        size: String(size),
      });
      params.append("sort", "ranking,asc");
      params.append("sort", "searchCount,desc");

      return apiClient
        .get(`${API_V1}/popular-locations`, {
          params,
          ...withActorHeaders(actorId, {
            [SKIP_WORKSPACE_CONTEXT_HEADER]: "true",
            ...withGuestAuth(authToken),
          }),
        })
        .then(unwrap)
        .then(pageContent);
    },
  },

  jobOpportunities: {
    generateDescription: async (data, actorId) =>
      apiClient
        .post(`${API_V1}/job-opportunities/ai/generate-description`, data, withActor(actorId))
        .then(unwrap),

    listEmployer: async ({
      actorId,
      posterId,
      workspaceId,
      status,
      page = 0,
      size = 20,
      sort = "createdAt,desc",
    } = {}) => {
      const path = posterId ? `/poster/${encodeURIComponent(posterId)}` : "";
      if (!path && !workspaceId)
        throw new Error("A poster or workspace is required to load job posts.");
      return apiClient
        .get(`${API_V1}/job-opportunities${path}`, {
          params: {
            page,
            size,
            sort,
            workspaceId: workspaceId || undefined,
            status: status || undefined,
          },
          ...withActorHeaders(actorId || posterId),
        })
        .then(unwrap)
        .then(normalizePage);
    },

    listRecent: async ({ actorId, authToken, ...filters } = {}) => {
      const { params, hasAdvancedSearch } = jobOpportunitySearchParams(filters);
      const path = hasAdvancedSearch ? "/search" : "";
      return apiClient
        .get(`${API_V1}/job-opportunities${path}`, {
          params,
          ...withActorHeaders(actorId, {
            [SKIP_WORKSPACE_CONTEXT_HEADER]: "true",
            ...withGuestAuth(authToken),
          }),
        })
        .then(unwrap)
        .then(normalizePage);
    },

    create: async (data, actorId) =>
      apiClient
        .post(`${API_V1}/job-opportunities`, data, withActor(actorId))
        .then(unwrap),

    update: async (jobOpportunityId, data, actorId) =>
      apiClient
        .put(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    patch: async (jobOpportunityId, data, actorId) =>
      apiClient
        .patch(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    get: async (jobOpportunityId, actorId, authToken) =>
      apiClient
        .get(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}`,
          withActorHeaders(actorId, withGuestAuth(authToken)),
        )
        .then(unwrap),

    listInterviewSessions: async (jobOpportunityId, actorId) =>
      apiClient
        .get(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/interview-sessions`,
          withActor(actorId),
        )
        .then(unwrap)
        .then(pageContent),

    createInterviewSessions: async (jobOpportunityId, data, actorId) =>
      apiClient
        .post(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/interview-sessions`,
          data,
          withActor(actorId),
        )
        .then(unwrap)
        .then(pageContent),

    updateInterviewSession: async (
      jobOpportunityId,
      interviewSessionId,
      data,
      actorId,
    ) =>
      apiClient
        .patch(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/interview-sessions/${encodeURIComponent(interviewSessionId)}`,
          data,
          withActor(actorId),
        )
        .then(unwrap),
  },

  jobApplications: {
    create: async (data, actorId) =>
      apiClient
        .post(`${API_V1}/job-applications`, data, {
          ...withActorHeaders(actorId, {
            [SKIP_WORKSPACE_CONTEXT_HEADER]: "true",
          }),
        })
        .then(unwrap),

    get: async (applicationId, actorId) =>
      apiClient
        .get(
          `${API_V1}/job-applications/${encodeURIComponent(applicationId)}`,
          withActor(actorId),
        )
        .then(unwrap),

    updateStatus: async (applicationId, data, actorId) =>
      apiClient
        .patch(
          `${API_V1}/job-applications/${encodeURIComponent(applicationId)}/status`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    listInterviewSessions: async (applicationId, actorId) =>
      apiClient
        .get(
          `${API_V1}/job-applications/${encodeURIComponent(applicationId)}/interview-sessions`,
          withActor(actorId),
        )
        .then(unwrap)
        .then(pageContent),

    createInterviewSession: async (applicationId, data, actorId) =>
      apiClient
        .post(
          `${API_V1}/job-applications/${encodeURIComponent(applicationId)}/interview-sessions`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    updateInterviewSession: async (
      applicationId,
      interviewSessionId,
      data,
      actorId,
    ) =>
      apiClient
        .patch(
          `${API_V1}/job-applications/${encodeURIComponent(applicationId)}/interview-sessions/${encodeURIComponent(interviewSessionId)}`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    listMine: async ({
      actorId,
      applicantId,
      userId,
      workspaceId,
      jobOpportunityId,
      status,
      page = 0,
      size = 20,
      sort = "createdAt,desc",
    } = {}) => {
      const targetUserId = userId || applicantId || actorId;
      if (!targetUserId)
        throw new Error("A user is required to load job applications.");
      if (!workspaceId)
        throw new Error("A workspace is required to load job applications.");

      return apiClient
        .get(
          `${API_V1}/job-applications/users/${encodeURIComponent(targetUserId)}`,
          {
            params: {
              page,
              size,
              sort,
              workspaceId,
              jobOpportunityId: jobOpportunityId || undefined,
              status: status || undefined,
            },
            ...withActorHeaders(actorId || targetUserId),
          },
        )
        .then(unwrap)
        .then(normalizePage);
    },

    listPostedJobs: async ({
      actorId,
      workspaceId,
      status,
      page = 0,
      size = 20,
      sort = "createdAt,desc",
    } = {}) => {
      if (!workspaceId)
        throw new Error(
          "A workspace is required to load posted job applications.",
        );
      return apiClient
        .get(`${API_V1}/job-applications/posted-jobs`, {
          params: {
            page,
            size,
            sort,
            workspaceId,
            status: status || undefined,
          },
          ...withActorHeaders(actorId),
        })
        .then(unwrap)
        .then(normalizePage);
    },

    listForJobPost: async ({
      actorId,
      workspaceId,
      jobOpportunityId,
      applicantId,
      status,
      page = 0,
      size = 20,
      sort = "createdAt,desc",
    } = {}) => {
      if (!jobOpportunityId)
        throw new Error("A job post is required to load applications.");
      return apiClient
        .get(
          `${API_V1}/job-applications/job-opportunities/${encodeURIComponent(jobOpportunityId)}`,
          {
            params: {
              page,
              size,
              sort,
              workspaceId: workspaceId || undefined,
              applicantId: applicantId || undefined,
              status: status || undefined,
            },
            ...withActorHeaders(actorId),
          },
        )
        .then(unwrap)
        .then(normalizePage);
    },
  },

  interviewSessions: {
    listForUser: async ({
      actorId,
      userId,
      workspaceId,
      page = 0,
      size = 20,
      sort = "interviewDateTime,desc",
    } = {}) => {
      const targetUserId = userId || actorId;
      if (!targetUserId)
        throw new Error("A user is required to load interviews.");

      return apiClient
        .get(
          `${API_V1}/job-applications/users/${encodeURIComponent(targetUserId)}/interview-sessions`,
          {
            params: {
              page,
              size,
              sort,
              workspaceId: workspaceId || undefined,
            },
            ...withActorHeaders(actorId || targetUserId),
          },
        )
        .then(unwrap)
        .then(normalizePage);
    },

    listForWorkspace: async ({
      actorId,
      workspaceId,
      jobOpportunityId,
      page = 0,
      size = 20,
      sort = "interviewDateTime,desc",
    } = {}) => {
      if (!workspaceId)
        throw new Error("A workspace is required to load interviews.");

      return apiClient
        .get(
          `${API_V1}/job-applications/workspaces/${encodeURIComponent(workspaceId)}/interview-sessions`,
          {
            params: {
              page,
              size,
              sort,
              jobOpportunityId: jobOpportunityId || undefined,
            },
            ...withActorHeaders(actorId),
          },
        )
        .then(unwrap)
        .then(normalizePage);
    },
  },

  jobOpportunityQuestions: {
    list: async (jobOpportunityId, actorId, authToken) =>
      apiClient
        .get(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/questions`,
          withActorHeaders(actorId, {
            [SKIP_WORKSPACE_CONTEXT_HEADER]: "true",
            ...withGuestAuth(authToken),
          }),
        )
        .then(unwrap)
        .then(pageContent),

    create: async (jobOpportunityId, data, actorId) =>
      apiClient
        .post(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/questions`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    update: async (jobOpportunityId, questionId, data, actorId) =>
      apiClient
        .patch(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/questions/${encodeURIComponent(questionId)}`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    delete: async (jobOpportunityId, questionId, actorId) =>
      apiClient
        .delete(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/questions/${encodeURIComponent(questionId)}`,
          withActor(actorId),
        )
        .then(unwrap),

    samples: async (actorId, { page = 0, size = 1000 } = {}) =>
      apiClient
        .get(`${API_V1}/sample-questions`, {
          params: { page, size, sort: "createdAt,asc" },
          ...withActor(actorId),
        })
        .then(unwrap)
        .then(pageContent)
        .then((questions) =>
          questions.filter((question) => Boolean(question?.question?.trim())),
        ),
  },

  jobOpportunityHireMembers: {
    list: async (jobOpportunityId, actorId, authToken) =>
      apiClient
        .get(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/hire-members`,
          withActorHeaders(actorId, withGuestAuth(authToken)),
        )
        .then(unwrap)
        .then(pageContent),

    create: async (jobOpportunityId, data, actorId) =>
      apiClient
        .post(
          `${API_V1}/job-opportunities/${encodeURIComponent(jobOpportunityId)}/hire-members`,
          data,
          withActor(actorId),
        )
        .then(unwrap),
  },

  professionalCatalogs: {
    specializations: async (actorId) =>
      apiClient
        .get(`${API_V1}/professional-specializations`, withActor(actorId))
        .then(unwrap)
        .then(pageContent)
        .then((items) => items.filter((item) => item?.active !== false)),

    skills: async (actorId) =>
      apiClient
        .get(`${API_V1}/professional-skills`, withActor(actorId))
        .then(unwrap)
        .then(pageContent)
        .then((items) => items.filter((item) => item?.active !== false)),
  },

  professionRoles: {
    list: async (actorId, authToken) =>
      apiClient
        .get(
          `${API_V1}/profession-roles`,
          withActorHeaders(actorId, withGuestAuth(authToken)),
        )
        .then(unwrap)
        .then(pageContent)
        .then((roles) => roles.filter((role) => Boolean(role?.title?.trim()))),
  },

  professionalRoles: {
    list: async ({ actorId, authToken } = {}) =>
      apiClient
        .get(
          `${API_V1}/professional-roles`,
          withActorHeaders(actorId, {
            [SKIP_WORKSPACE_CONTEXT_HEADER]: "true",
            ...withGuestAuth(authToken),
          }),
        )
        .then(unwrap)
        .then(pageContent)
        .then((roles) => roles.filter((role) => Boolean(role?.title?.trim()))),
  },

  backgroundChecks: {
    listForProfessionProfile: async (
      professionProfileId,
      checkType,
      options = {},
    ) =>
      apiClient
        .get(
          `${API_V1}/background-checks/profession-profiles/${professionProfileId}`,
          {
            params: {
              ...(checkType ? { checkType } : {}),
              ...(options.userId ? { userId: options.userId } : {}),
            },
            ...withActorHeaders(options.actorId || options.userId),
          },
        )
        .then(unwrap),

    get: async (backgroundCheckId) =>
      apiClient
        .get(`${API_V1}/background-checks/${backgroundCheckId}`)
        .then(unwrap),

    listForUser: async ({ userId, actorId, checkType } = {}) =>
      apiClient
        .get(
          `${API_V1}/background-checks/users/${encodeURIComponent(userId || actorId)}`,
          {
            params: checkType ? { checkType } : undefined,
            ...withActorHeaders(actorId || userId),
          },
        )
        .then(unwrap)
        .then(pageContent),

    requestForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(
          `${API_V1}/background-checks/profession-profiles/${professionProfileId}/request`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    initiateForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(
          `${API_V1}/background-checks/profession-profiles/${professionProfileId}/initiate`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    createForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(
          `${API_V1}/background-checks/profession-profiles/${professionProfileId}`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    update: async (backgroundCheckId, data, actorId) =>
      apiClient
        .put(
          `${API_V1}/background-checks/${backgroundCheckId}`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    initiate: async (backgroundCheckId, actorId) =>
      apiClient
        .post(
          `${API_V1}/background-checks/${backgroundCheckId}/initiate`,
          null,
          withActor(actorId),
        )
        .then(unwrap),

    cancel: async (backgroundCheckId, actorId) =>
      apiClient
        .put(
          `${API_V1}/background-checks/${backgroundCheckId}/cancel`,
          null,
          withActor(actorId),
        )
        .then(unwrap),

    submitItemResult: async (backgroundCheckId, data, actorId) =>
      apiClient
        .patch(
          `${API_V1}/background-checks/${backgroundCheckId}/check`,
          data,
          withActor(actorId),
        )
        .then(unwrap),
  },

  helperProfiles: {
    list: async (params = {}) =>
      apiClient.get(`${API_V1}/helper-profiles`, { params }).then(unwrap),

    get: async (userId) =>
      apiClient.get(`${API_V1}/helper-profiles/${userId}`).then(unwrap),

    updateStatus: async (userId, data) =>
      apiClient
        .put(`${API_V1}/helper-profiles/${userId}/status`, data)
        .then(unwrap),
  },

  professionProfiles: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles`, {
          params: withPagination(params),
        })
        .then(unwrap),

    get: async (professionProfileId, authToken) =>
      apiClient
        .get(`${API_V1}/profession-profiles/${professionProfileId}`, {
          headers: withGuestAuth(authToken),
        })
        .then(unwrap),

    searchTalent: async ({
      actorId,
      authToken,
      query,
      sector,
      educationLevel,
      page = 0,
      size = 20,
    } = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles/talent-search`, {
          params: {
            q: query?.trim() || undefined,
            sector: sector || undefined,
            educationLevel: educationLevel || undefined,
            page,
            size,
          },
          ...withActorHeaders(actorId, withGuestAuth(authToken)),
        })
        .then(unwrap)
        .then(normalizePage),

    listSectors: async (actorId) =>
      apiClient
        .get(`${API_V1}/profession-sectors`, withActor(actorId))
        .then(unwrap)
        .then(pageContent),

    listServiceOfferings: async (actorId, params = {}) =>
      apiClient
        .get(`${API_V1}/service-offerings`, {
          params: withPagination({ page: 0, size: 5000, ...params }),
          ...withActor(actorId),
        })
        .then(unwrap)
        .then(pageContent),

    listByUser: async (userId, params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles`, {
          params: { ...withPagination(params), userId: userId || undefined },
        })
        .then(unwrap),

    listForUser: async (userId, params = {}) => {
      const { workspaceId, ...rest } = params;
      if (workspaceId) {
        try {
          const result = await apiClient
            .get(
              `${API_V1}/profession-profiles/users/${encodeURIComponent(userId)}`,
              {
                params: { workspaceId },
              },
            )
            .then(unwrap);
          if (pageContent(result).length || Array.isArray(result))
            return result;
        } catch {
          // Fall back to the query endpoint used by older web surfaces.
        }
      }
      return olreadiaService.professionProfiles.listByUser(userId, {
        ...rest,
        workspaceId,
      });
    },

    listRequirements: async (params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles/requirements`, { params })
        .then(unwrap),

    createForUser: async (userId, data) =>
      apiClient
        .post(`${API_V1}/profession-profiles`, data, {
          params: { userId: userId || undefined },
        })
        .then(unwrap),

    updateForUser: async (userId, data) =>
      apiClient
        .put(`${API_V1}/profession-profiles`, data, {
          params: { userId: userId || undefined },
        })
        .then(unwrap),

    saveForUser: async (userId, data, actorId) =>
      apiClient
        .put(`${API_V1}/profession-profiles`, data, {
          params: userId && userId !== actorId ? { userId } : undefined,
          ...withActorHeaders(actorId || userId),
        })
        .then(unwrap),

    patchSection: async (section, data, actorId, userId, options = {}) => {
      let payload = options.mediaPatchMode
        ? { ...data, mediaPatchMode: options.mediaPatchMode }
        : data;
      if (MEDIA_SECTIONS.has(section) && data?.id && !options.mediaPatchMode) {
        const latestProfile = await olreadiaService.professionProfiles.get(
          data.id,
        );
        const mergedMediaList = await buildProfileMediaPatchList({
          section,
          submitted: data?.[section],
          latest: latestProfile?.[section],
          previous: options.previousProfile?.[section],
        });
        payload = {
          ...data,
          [section]: mergedMediaList,
        };
      }

      const patchRequest = () =>
        apiClient.patch(`${API_V1}/profession-profiles/${section}`, payload, {
          params: userId && userId !== actorId ? { userId } : undefined,
          ...withActorHeaders(actorId || userId),
        });

      return (
        MEDIA_SECTIONS.has(section)
          ? withFilesReadyRetry(patchRequest, { retries: 5, delayMs: 1000 })
          : patchRequest()
      ).then(unwrap);
    },

    delete: async (professionProfileId, params = {}) =>
      apiClient
        .delete(`${API_V1}/profession-profiles/${professionProfileId}`, {
          params,
        })
        .then(unwrap),
  },

  organizations: {
    list: async (params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/organizations`, {
          params: withPagination(params),
        })
        .then(unwrap)
        .then(normalizeOrganizationPage),

    listForUser: async (actorId, params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/organizations/user`, {
          params: withPagination(params),
          ...withActor(actorId),
        })
        .then(unwrap)
        .then(normalizeOrganizationPage),

    get: async (organizationId, params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/organizations/${organizationId}`, { params })
        .then(unwrap)
        .then(normalizeOrganizationResponse),

    create: async (data, actorId) =>
      apiClient
        .post(
          `${PROFILE_API_V1}/organizations`,
          normalizeOrganizationPayload(data),
          withActor(actorId),
        )
        .then(unwrap)
        .then(normalizeOrganizationResponse),

    update: async (organizationId, data, actorId) =>
      apiClient
        .patch(
          `${PROFILE_API_V1}/organizations/${organizationId}`,
          normalizeOrganizationPayload(data),
          withActor(actorId),
        )
        .then(unwrap)
        .then(normalizeOrganizationResponse),

    updateProp: async (organizationId, data, actorId) =>
      apiClient
        .patch(
          `${PROFILE_API_V1}/organizations/${organizationId}/props`,
          data,
          withActor(actorId),
        )
        .then(unwrap)
        .then((response) =>
          response ? normalizeOrganizationResponse(response) : response,
        ),

    delete: async (organizationId, actorId) =>
      apiClient
        .delete(
          `${PROFILE_API_V1}/organizations/${organizationId}`,
          withActor(actorId),
        )
        .then(unwrap),

    users: {
      list: async (organizationId, params = {}) =>
        apiClient
          .get(`${PROFILE_API_V1}/organizations/${organizationId}/users`, {
            params: withPagination(params),
          })
          .then(unwrap),

      add: async (organizationId, data, actorId) =>
        apiClient
          .post(
            `${PROFILE_API_V1}/organizations/${organizationId}/users`,
            data,
            withActor(actorId),
          )
          .then(unwrap),

      update: async (organizationId, userId, data, actorId) =>
        apiClient
          .patch(
            `${PROFILE_API_V1}/organizations/${organizationId}/users/${userId}`,
            data,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (organizationId, userId, actorId) =>
        apiClient
          .delete(
            `${PROFILE_API_V1}/organizations/${organizationId}/users/${userId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },

    locations: {
      list: async (organizationId, params = {}) =>
        apiClient
          .get(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations`,
            {
              params: withPagination(params),
            },
          )
          .then(unwrap)
          .then(normalizeOrganizationLocationPage),

      get: async (organizationId, businessLocationId) =>
        apiClient
          .get(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations/${businessLocationId}`,
          )
          .then(unwrap)
          .then(normalizeOrganizationLocationResponse),

      create: async (organizationId, data, actorId) =>
        apiClient
          .post(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations`,
            normalizeBusinessLocationPayload(data),
            withActor(actorId),
          )
          .then(unwrap)
          .then(normalizeOrganizationLocationResponse),

      update: async (organizationId, businessLocationId, data, actorId) =>
        apiClient
          .patch(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations/${businessLocationId}`,
            normalizeBusinessLocationPayload(data),
            withActor(actorId),
          )
          .then(unwrap)
          .then(normalizeOrganizationLocationResponse),

      delete: async (organizationId, businessLocationId, actorId) =>
        apiClient
          .delete(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations/${businessLocationId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },
  },

  businessLocationTypes: {
    list: async (params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/organization-location-types`, {
          params: withPagination({ size: 50, ...params }),
        })
        .then(unwrap),
  },

  serviceOfferings: {
    list: async (params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/service-offerings`, {
          params: withPagination(params),
        })
        .then(unwrap),

    get: async (serviceId) =>
      apiClient
        .get(`${PROFILE_API_V1}/service-offerings/${serviceId}`)
        .then(unwrap),

    create: async (data, actorId) =>
      apiClient
        .post(`${PROFILE_API_V1}/service-offerings`, data, withActor(actorId))
        .then(unwrap),

    update: async (serviceId, data, actorId) =>
      apiClient
        .patch(
          `${PROFILE_API_V1}/service-offerings/${serviceId}`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    delete: async (serviceId, actorId) =>
      apiClient
        .delete(
          `${PROFILE_API_V1}/service-offerings/${serviceId}`,
          withActor(actorId),
        )
        .then(unwrap),

    organizationServices: {
      list: async (organizationId) =>
        apiClient
          .get(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}`,
          )
          .then(unwrap),

      add: async (organizationId, serviceId, actorId) =>
        apiClient
          .post(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-offerings/${serviceId}`,
            null,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (organizationId, serviceId, actorId) =>
        apiClient
          .delete(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-offerings/${serviceId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },

    businessLocationServices: {
      list: async (organizationId, businessLocationId) =>
        apiClient
          .get(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-locations/${businessLocationId}`,
          )
          .then(unwrap),

      add: async (organizationId, businessLocationId, serviceId, actorId) =>
        apiClient
          .post(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-locations/${businessLocationId}/organization-offerings/${serviceId}`,
            null,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (organizationId, businessLocationId, serviceId, actorId) =>
        apiClient
          .delete(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-locations/${businessLocationId}/organization-offerings/${serviceId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },
  },

  websites: {
    templates: async ({ type, actorId } = {}) =>
      apiClient
        .get(`${COMPOSER_API_V1}/templates`, {
          params: type ? { type } : undefined,
          ...withActorHeaders(actorId),
        })
        .then(unwrap)
        .then(pageContent),

    getTemplate: async (templateId, actorId) =>
      apiClient
        .get(
          `${COMPOSER_API_V1}/templates/${encodeURIComponent(templateId)}`,
          withActorHeaders(actorId),
        )
        .then(unwrap),

    templatePreviewUrl: (templateId) =>
      `${COMPOSER_API_V1}/templates/${encodeURIComponent(templateId)}/preview`,

    previewUrl: (slug) => `http://dev.sites.olreadia.com/${slug}`,

    byWorkspace: async (workspaceId, actorId) =>
      apiClient
        .get(`${COMPOSER_API_V1}/websites`, {
          params: { wid: workspaceId, page: 0, size: 1 },
          ...withActorHeaders(actorId),
        })
        .then(unwrap)
        .then((data) => pageContent(data)[0] || (data?.id ? data : null))
        .catch((error) => {
          if (error?.response?.status === 404) return null;
          throw error;
        }),

    byUserPortfolio: async (workspaceId, actorId) =>
      apiClient
        .get(
          `${COMPOSER_API_V1}/websites/workspace/${encodeURIComponent(workspaceId)}`,
          withActorHeaders(actorId),
        )
        .then(unwrap)
        .catch((error) => {
          if (error?.response?.status === 404) return null;
          throw error;
        }),

    status: async (websiteId, actorId) =>
      apiClient
        .get(
          `${COMPOSER_API_V1}/websites/${encodeURIComponent(websiteId)}/status`,
          withActorHeaders(actorId),
        )
        .then(unwrap),

    activeContent: async (websiteId, actorId) =>
      apiClient
        .get(
          `${COMPOSER_API_V1}/websites/${encodeURIComponent(websiteId)}/content/active`,
          withActorHeaders(actorId),
        )
        .then(unwrap),

    detailsByWorkspace: async (workspaceId, actorId) => {
      const website = await olreadiaService.websites.byWorkspace(
        workspaceId,
        actorId,
      );
      if (!website) return null;
      const [status, activeContent] = await Promise.all([
        olreadiaService.websites.status(website.id, actorId).catch(() => null),
        olreadiaService.websites
          .activeContent(website.id, actorId)
          .catch(() => null),
      ]);
      let parsedContent = { sections: [] };
      try {
        parsedContent = activeContent?.contentJson
          ? JSON.parse(activeContent.contentJson)
          : parsedContent;
      } catch {
        parsedContent = { sections: [] };
      }
      return { website, status, activeContent, parsedContent };
    },

    detailsByUserPortfolio: async (userId, actorId) => {
      const website = await olreadiaService.websites.byUserPortfolio(
        userId,
        actorId,
      );
      if (!website) return null;
      const [status, activeContent] = await Promise.all([
        olreadiaService.websites.status(website.id, actorId).catch(() => null),
        olreadiaService.websites
          .activeContent(website.id, actorId)
          .catch(() => null),
      ]);
      let parsedContent = { sections: [] };
      try {
        parsedContent = activeContent?.contentJson
          ? JSON.parse(activeContent.contentJson)
          : parsedContent;
      } catch {
        parsedContent = { sections: [] };
      }
      return { website, status, activeContent, parsedContent };
    },

    uploadImageForWorkspace: async (
      workspaceId,
      file,
      slotType,
      actorId,
      onProgress,
    ) => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("slotType", slotType);

        const { data } = await apiClient.post(
          `${COMPOSER_API_V1}/assets/workspace/${encodeURIComponent(workspaceId)}/upload`,
          formData,
          {
            ...withActorHeaders(actorId),
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: onProgress
              ? (evt) =>
                  onProgress(
                    Math.round((evt.loaded / (evt.total || evt.loaded)) * 100),
                  )
              : undefined,
          },
        );

        return data.url;
      } catch (error) {
        console.log("upload failed", error);
        message.error("Image upload failed, try again");
      }
    },

    deleteSite: async (websiteId) => {
      try {
        await apiClient.delete(
          `${COMPOSER_API_V1}/websites/${encodeURIComponent(websiteId)}`,
        );
        message.success("Deleted successfully");
      } catch (err) {
        console.error("Delete failed:", err);
        message.error("Error occured, delete failed");
        throw err;
      }
    },

    checkSlug: (slug, actorId) =>
      apiClient
        .get(`${COMPOSER_API_V1}/websites/slug/check`, {
          params: { slug },
           ...withActorHeaders(actorId),
        })
        .then((res) => res.data),

    bootstrap: async (data, actorId) =>
      apiClient
        .post(
          `${COMPOSER_API_V1}/websites/bootstrap`,
          data,
          withActorHeaders(actorId),
        )
        .then(unwrap),

    regenerate: async (websiteId, template, overrides = {}, actorId) =>
      apiClient
        .post(
          `${COMPOSER_API_V1}/websites/${encodeURIComponent(websiteId)}/generate`,
          { overrides },
          {
            params: { template },
            ...withActorHeaders(actorId),
          },
        )
        .then(unwrap),
  },

  domains: {
    search: async (domain, tlds, actorId) =>
      apiClient
        .post(
          `${COMPOSER_API_V1}/domains/search`,
          { domain, tlds },
          withActorHeaders(actorId),
        )
        .then(unwrap),

    registrations: async (workspaceId, actorId) =>
      apiClient
        .get(
          `${COMPOSER_API_V1}/domains/workspaces/${encodeURIComponent(workspaceId)}/registrations`,
          withActorHeaders(actorId),
        )
        .then(unwrap),

    checkout: async (
      {
        workspaceId,
        websiteId,
        domain,
        periodYears = 1,
        privacy = true,
        autoRenew = true,
        marketId = "en-US",
      },
      actorId,
    ) =>
      apiClient
        .post(
          `${COMPOSER_API_V1}/domains/checkout`,
          {
            workspaceId,
            websiteId,
            domain,
            periodYears,
            privacy,
            autoRenew,
            marketId,
          },
          withActorHeaders(actorId),
        )
        .then(unwrap),
  },

  usage: {
    aiRequests: async (workspaceId, actorId) =>
      apiClient
        .get(`${API_V1}/usage/ai-requests`, {
          params: { workspaceId },
          ...withActor(actorId),
        })
        .then(unwrap),
  },
};

export default olreadiaService;
